import { HttpClient } from '@angular/common/http';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { languageTags } from '../consts';
import { PICFLOW_CONFIG } from '../picflow-config';
import { JsonLDType } from '../types/json-ls';
import { StoryblokService } from './storyblok.service';

interface OfferPriceCurrencyOptions {
  currency: string;
  unit_amount: number;
}

interface OfferPrice {
  id: string;
  currency_options: OfferPriceCurrencyOptions[];
  interval: string;
  metadata: Params;
}
interface Offer {
  id: string;
  name: string;
  metadata: Params;
  prices: OfferPrice[];
}

@Injectable({
  providedIn: 'root',
})
export class JsonLDService {
  private document = inject(DOCUMENT);
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private storyblokService = inject(StoryblokService);

  public get(type: JsonLDType): Params | null {
    const script = this.document.getElementById(`json-ld-${type}`);
    if (!script) {
      return null;
    }

    try {
      return JSON.parse(script.textContent || '{}') as Params;
    } catch (e) {
      console.error(`Failed to parse JSON-LD for type "${type}":`, e);
      return null;
    }
  }

  /**
   * Adds a JSON-LD script tag to the document head.
   * @param data
   * @return an identifier for the script that can be used to remove it later.
   */
  public add(type: JsonLDType, data: Params): string {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `json-ld-${type}`;
    script.textContent = JSON.stringify(data, null, 2);

    const existingScript = this.document.getElementById(script.id);
    if (existingScript) {
      existingScript.remove();
    }

    if (type === 'global') {
      const firstJsonLd = this.document.head.querySelector('script[type="application/ld+json"]');
      if (firstJsonLd) {
        this.document.head.insertBefore(script, firstJsonLd);
        return script.id;
      }
    }

    this.document.head.appendChild(script);
    return script.id;
  }

  public remove(type: JsonLDType): void {
    const script = this.document.getElementById(`json-ld-${type}`);
    if (script) {
      script.remove();
    }
  }

  private async getOrganizationSchema() {
    return {
      '@type': 'Organization',
      '@id': 'https://picflow.com/#organization',
      name: 'Picflow',
      url: 'https://picflow.com',
      logo: {
        '@type': 'ImageObject',
        url: `${this.config.correctPicflowUrl}/assets/picflow-logo-400.png`,
        width: 400,
        height: 400,
      },
      sameAs: [
        'https://x.com/PicflowHQ',
        'https://www.instagram.com/picflow/',
        'https://www.youtube.com/@Picflow',
        'https://www.linkedin.com/company/picflow/',
        'https://www.facebook.com/picflow/',
        'https://crunchbase.com/organization/picflow',
        'https://wikidata.org/wiki/Q124683139',
        'https://apps.apple.com/app/picflow/id6741537409',
        'https://play.google.com/store/apps/details?id=com.picflow.pwa.twa',
        'https://www.g2.com/products/picflow/reviews',
        'https://www.capterra.com/p/10008708/Picflow/',
        'https://www.producthunt.com/products/picflow',
      ],
      description:
        'Picflow is the online proofing platform for creatives. Share branded review links, track approvals, and transfer media.',
      foundingDate: '2022',
      knowsAbout: [
        'Client Gallery Software',
        'Client Proofing',
        'Creative Collaboration',
        'Creative Review Software',
        'Creative Workflow Management',
        'Digital Asset Management',
        'Marketing Asset Management',
        'Media Asset Management',
        'Online Proofing Software',
        'Photo Proofing',
        'Video Proofing',
      ],
      legalName: 'Nusign AG',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Schöneggstrasse',
        postalCode: '8004',
        addressLocality: 'Zurich',
        addressCountry: 'CH',
      },
      areaServed: 'Worldwide',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          contactOption: 'TollFree',
          email: 'help@picflow.com',
          url: 'https://help.picflow.com',
          availableLanguage: languageTags,
        },
      ],
    };
  }

  private async getWebSiteSchema() {
    return {
      '@type': 'WebSite',
      '@id': 'https://picflow.com/#website',
      name: 'Picflow',
      url: 'https://picflow.com',
      publisher: { '@id': 'https://picflow.com/#organization' },
      inLanguage: languageTags,
      // TODO: Add SearchAction once public search is available
    };
  }

  private async getOffers() {
    try {
      const [offers, storyblokProducts] = await Promise.all([
        firstValueFrom(this.http.get<Offer[]>(`${this.config.internalApiUrlBase}/offers`)),
        firstValueFrom(this.storyblokService.getStories({ content_type: 'product' }).pipe(map(resp => resp.stories))),
      ]);

      const mappedOffers = offers
        .filter(offer => offer.prices.length)
        .map(product => {
          const yearlyPrice = product.prices.find(price => price.interval === 'year');
          if (!yearlyPrice) return;
          const yearlyAmount = yearlyPrice.currency_options.find(option => option.currency === 'usd')?.unit_amount;
          if (yearlyAmount === undefined) return;
          const productSlug = product.name.replace('Picflow ', '').toLowerCase();

          const storyblokProduct = storyblokProducts.find(p => productSlug === p.slug);
          if (!storyblokProduct) return;

          return {
            '@type': 'Offer',
            name: `Picflow ${storyblokProduct.name}`,
            description: storyblokProduct.content.description,
            url: 'https://picflow.com/pricing',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: yearlyAmount / 100 / 12,
              priceCurrency: 'USD',
              referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
            },
          };
        })
        .filter(Boolean);

      const maxPrice = Math.max(...mappedOffers.map(offer => offer.priceSpecification.price));

      return {
        '@type': 'AggregateOffer',
        price: 0,
        lowPrice: 0,
        highPrice: maxPrice,
        priceCurrency: 'USD',
        offerCount: 5,
        offers: [
          {
            '@type': 'Offer',
            name: 'Picflow Free',
            description: 'For individuals getting started',
            price: 0,
            priceCurrency: 'USD',
            url: 'https://picflow.com/pricing',
          },
          ...mappedOffers,
          {
            '@type': 'Offer',
            name: 'Enterprise',
            description: 'Custom pricing for teams and agencies',
            url: 'https://picflow.com/pricing',
          },
        ],
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  public async getSoftwareApplicationSchema() {
    return {
      '@type': 'SoftwareApplication',
      '@id': 'https://picflow.com/#software',
      name: 'Picflow',
      url: 'https://picflow.com',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android',
      description:
        'The online proofing platform for creatives. Share branded review links, track approvals, and transfer media.',
      publisher: { '@id': 'https://picflow.com/#organization' },
      featureList: [
        'Approval Statuses',
        'Approval Workflows',
        'Auto-Tagging',
        'Branded Client Galleries',
        'Custom Branding',
        'File Transfer',
        'Gallery Embed Widget',
        'Image Annotations',
        'Password Protection',
        'RAW Photo Support',
        'Version Review',
        'Video Annotations',
        'Watermarking',
      ],
      offers: await this.getOffers(),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '249',
        bestRating: '5',
        worstRating: '1',
      },
      downloadUrl: [
        'https://apps.apple.com/app/picflow/id6741537409',
        'https://play.google.com/store/apps/details?id=com.picflow.pwa.twa',
      ],
      screenshot: `${this.config.correctPicflowUrl}/assets/shared/default.jpg`,
      softwareHelp: { '@type': 'CreativeWork', url: 'https://help.picflow.com' },
      releaseNotes: 'https://picflow.com/updates',
    };
  }

  private async getGlobalSchema() {
    return {
      '@context': 'https://schema.org',
      '@graph': [await this.getWebSiteSchema()],
    };
  }

  public async getHomePageSchema() {
    return {
      '@context': 'https://schema.org',
      '@graph': [await this.getSoftwareApplicationSchema(), await this.getOrganizationSchema()],
    };
  }

  private async addGlobalSchema() {
    const globalSchema = await this.getGlobalSchema();
    this.add('global', globalSchema);
  }

  /** Recursively extracts plain text from a Storyblok rich-text node tree. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private richTextToPlainText(node: any): string {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (node.text) return node.text;
    if (Array.isArray(node.content)) {
      return node.content
        .map((n: unknown) => this.richTextToPlainText(n))
        .join('')
        .trim();
    }
    return '';
  }

  /**
   * Adds schema.org Review structured data for Picflow.
   *
   * Called by ReviewListComponent and ReviewCarouselComponent. Multiple calls
   * on the same page are merged (reviews accumulate rather than replace).
   *
   * Placement strategy:
   *  - If the page JSON-LD is already a SoftwareApplication (@id: #software),
   *    reviews are nested directly into it.
   *  - Otherwise a separate 'reviews' JSON-LD block is emitted with a full
   *    SoftwareApplication entity so the @id resolves on every page.
   */
  public async addReviewsSchema(
    reviews: { name: string; role?: string; company?: string; body: unknown; rating: number; datePublished?: string }[]
  ) {
    if (!reviews.length) return;

    const reviewSchemas = reviews.map(review => {
      const author: Params = { '@type': 'Person', name: review.name };
      if (review.role) author['jobTitle'] = review.role;
      if (review.company) author['worksFor'] = { '@type': 'Organization', name: review.company };

      const schema: Params = {
        '@type': 'Review',
        author,
        reviewBody: this.richTextToPlainText(review.body),
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.rating.toString(),
          bestRating: '5',
          worstRating: '1',
        },
      };
      if (review.datePublished) schema['datePublished'] = review.datePublished.split('T')[0];
      return schema;
    });

    const pageLd = this.get('page');

    if (pageLd && pageLd['@id'] === 'https://picflow.com/#software') {
      // Merge into the existing SoftwareApplication page entity
      const existing = Array.isArray(pageLd['review']) ? pageLd['review'] : [];
      this.add('page', { ...pageLd, review: [...existing, ...reviewSchemas] });
    }
  }

  constructor() {
    if (this.config.isTenantDomain) return;
    this.addGlobalSchema();
  }
}
